import { useMemo, useCallback, useRef, memo, useEffect, useState } from "react"
import { useForm, Controller, type UseFormRegister } from "react-hook-form"
import {
  Button,
  Input,
  Textarea,
  VStack,
  Field,
  HStack,
  Text,
  Box,
  Icon,
  Separator,
} from "@chakra-ui/react"
import { NativeSelectRoot, NativeSelectField } from "@chakra-ui/react"
import { CollapsibleRoot, CollapsibleTrigger, CollapsibleContent } from "@chakra-ui/react"
import { FaChevronDown, FaFilePen, FaCode } from "react-icons/fa6"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import type { AssignableUser, KanbanAlert, AlertEvent } from "./AlertCard"
import type { UpdateAlertInput, AlertStatus } from "./types"
import { useIsAdmin } from "@hydrologiq/shared/providers/helpers/useUserRole"

function formatNoteDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return dateStr
  }
}

function getUserDisplayName(user?: { name?: string; email?: string } | null): string {
  if (!user) return "System"
  if (user.name) return user.name
  if (user.email) return user.email
  return "Unknown User"
}

interface FormValues {
  name: string
  severity: "critical" | "warning" | "info"
  status: AlertStatus
  note: string
  assigneeUserId: string
  metadata: Record<string, unknown>
}

function assignableOptionLabel(u: AssignableUser): string {
  return (u.name || "").trim() || (u.email || "").trim() || u.id
}

interface EditFieldsProps {
  register: UseFormRegister<FormValues>
  assignableUsers?: AssignableUser[]
  isNameDisabled?: boolean
}

const EditFields = memo(function EditFields({
  register,
  assignableUsers = [],
  isNameDisabled = false,
}: EditFieldsProps) {
  return (
    <VStack align="stretch" gap={3} width="100%">
      <Field.Root width="100%">
        <Field.Label>Name</Field.Label>
        <Input
          width="100%"
          placeholder="Alert name"
          required
          disabled={isNameDisabled}
          style={isNameDisabled ? { opacity: 0.6 } : undefined}
          {...register("name")}
        />
      </Field.Root>

      <Field.Root width="100%">
        <Field.Label>Severity</Field.Label>
        <NativeSelectRoot width="100%">
          <NativeSelectField width="100%" {...register("severity")}>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </NativeSelectField>
        </NativeSelectRoot>
      </Field.Root>

      <Field.Root width="100%">
        <Field.Label>Status</Field.Label>
        <NativeSelectRoot width="100%">
          <NativeSelectField width="100%" {...register("status")}>
            <option value="new">New</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="attended">Attended</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Resolved</option>
          </NativeSelectField>
        </NativeSelectRoot>
      </Field.Root>

      {assignableUsers.length > 0 && (
        <Field.Root width="100%">
          <Field.Label>Assignee</Field.Label>
          <NativeSelectRoot width="100%">
            <NativeSelectField width="100%" {...register("assigneeUserId")}>
              <option value="">Unassigned</option>
              {assignableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {assignableOptionLabel(u)}
                </option>
              ))}
            </NativeSelectField>
          </NativeSelectRoot>
        </Field.Root>
      )}
    </VStack>
  )
})

interface NotesSectionProps {
  register: UseFormRegister<FormValues>
  noteEvents: AlertEvent[]
}

const NotesSection = memo(function NotesSection({
  register,
  noteEvents,
}: NotesSectionProps) {
  return (
    <Box width="100%" bg="bg.subtle" p={3} borderRadius="md">
      <VStack align="stretch" gap={3} width="100%">
        <Text fontSize="sm" fontWeight="medium" color="primary">
          Notes
        </Text>
        <Field.Root width="100%">
          <Field.Label>Add Note (optional)</Field.Label>
          <Textarea
            width="100%"
            placeholder="Add a note about this alert..."
            rows={3}
            {...register("note")}
          />
        </Field.Root>

        {noteEvents.length > 0 && (
          <CollapsibleRoot defaultOpen={false}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                width="100%"
                justifyContent="space-between"
              >
                <HStack gap={2}>
                  <Icon as={FaFilePen} boxSize={3} />
                  <Text>Notes History ({noteEvents.length})</Text>
                </HStack>
                <Icon
                  as={FaChevronDown}
                  boxSize={3}
                  transition="transform 0.2s"
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <VStack align="stretch" gap={2} mt={2} maxH="200px" overflowY="auto" width="100%">
                {noteEvents.map((event) => (
                  <Box
                    key={event.id}
                    p={2}
                    bg="card"
                    borderRadius="md"
                    borderLeft="2px solid"
                    borderLeftColor="gray.300"
                  >
                    <VStack align="stretch" gap={1}>
                      <HStack justify="space-between" fontSize="xs" color="fg.muted">
                        <Text fontWeight="medium">
                          {getUserDisplayName(event.user)}
                        </Text>
                        <Text>{formatNoteDate(event.created_at)}</Text>
                      </HStack>
                      {event.note && (
                        <Text fontSize="sm" color="primary">
                          {event.note}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                ))}
              </VStack>
            </CollapsibleContent>
          </CollapsibleRoot>
        )}
      </VStack>
    </Box>
  )
})

interface AlertEditDialogProps {
  alert: KanbanAlert
  open: boolean
  setOpen: (open: boolean) => void
  onUpdate: (alertId: string, input: UpdateAlertInput) => Promise<void>
  isUpdating?: boolean
  assignableUsers?: AssignableUser[]
}

function currentAssigneeId(a: KanbanAlert): string {
  return a.assignee?.id ?? a.assigned_to ?? ""
}

export function AlertEditDialog({
  alert,
  open,
  setOpen,
  onUpdate,
  isUpdating = false,
  assignableUsers = [],
}: AlertEditDialogProps) {
  const isAdmin = useIsAdmin()

  const { register, handleSubmit, reset, control, setValue } = useForm<FormValues>({
    defaultValues: {
      name: alert.name,
      severity: alert.severity as "critical" | "warning" | "info",
      status: (alert.status || "new") as AlertStatus,
      note: "",
      assigneeUserId: currentAssigneeId(alert),
      metadata: alert.metadata ?? {},
    },
  })

  // Track whether the dialog was previously open to detect fresh opens
  const prevOpenRef = useRef(false)
  const prevAlertIdRef = useRef(alert.id)

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current
    const alertChanged = alert.id !== prevAlertIdRef.current

    prevOpenRef.current = open
    prevAlertIdRef.current = alert.id

    // Only reset form when the dialog freshly opens or the alert ID changes.
    // This prevents blowing away in-progress edits when the parent refetches
    // with identical data (re-render stability).
    if (open && (justOpened || alertChanged)) {
      reset({
        name: alert.name,
        severity: alert.severity as "critical" | "warning" | "info",
        status: (alert.status || "new") as AlertStatus,
        note: "",
        assigneeUserId: currentAssigneeId(alert),
        metadata: alert.metadata ?? {},
      })
    }
  }, [open, alert.id, alert.name, alert.severity, alert.assignee, alert.assigned_to, alert.metadata, reset])

  const noteEvents = useMemo(() => {
    if (!alert.events) return []
    return alert.events
      .filter((e) => e.kind === "note" || e.kind === "assigned")
      .reverse()
  }, [alert.events])

  const onValidSubmit = useCallback(
    async (data: FormValues) => {
      const input: UpdateAlertInput = {}
      let hasUpdates = false

      if (data.name !== alert.name) {
        input.name = data.name
        hasUpdates = true
      }

      if (data.severity !== alert.severity) {
        input.severity = data.severity
        hasUpdates = true
      }

      if (data.status !== (alert.status || "new")) {
        input.status = data.status
        hasUpdates = true
      }

      if (data.note.trim()) {
        input.note = data.note.trim()
        hasUpdates = true
      }

      const priorAssignee = currentAssigneeId(alert)
      if (data.assigneeUserId !== priorAssignee) {
        input.assignedToUserId =
          data.assigneeUserId === "" ? null : data.assigneeUserId
        hasUpdates = true
      }

      if (isAdmin && JSON.stringify(data.metadata) !== JSON.stringify(alert.metadata ?? {})) {
        input.metadata = data.metadata
        hasUpdates = true
      }

      if (!hasUpdates) {
        setOpen(false)
        return
      }

      try {
        await onUpdate(alert.id, input)
        setOpen(false)
      } catch (error) {
        // Error is handled by the hook
      }
    },
    [alert, setOpen, onUpdate, isAdmin]
  )

  const handleSubmitClick = useCallback(() => {
    handleSubmit(onValidSubmit)()
  }, [handleSubmit, onValidSubmit])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen)
    },
    [setOpen]
  )

  return (
    <Dialog
      title="Edit Alert"
      open={open}
      setOpen={handleOpenChange}
      size="xl"
      showContinueButton
      continueLabel="Save"
      onContinue={handleSubmitClick}
      continueDisabled={isUpdating}
      loading={isUpdating}
    >
      <Box width="100%">
        <VStack
          key={open ? alert.id : "closed"}
          align="stretch"
          gap={3}
          width="100%"
        >
          {isAdmin && (
            <CollapsibleRoot>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  width="100%"
                  justifyContent="space-between"
                  p={3}
                  bg="bg.subtle"
                  borderRadius="md"
                >
                  <HStack gap={2}>
                    <Icon as={FaCode} color="fg.muted" />
                    <Text fontSize="sm" fontWeight="medium">
                      Edit Metadata (Admin)
                    </Text>
                  </HStack>
                  <Icon
                    as={FaChevronDown}
                    boxSize={3}
                    transition="transform 0.2s"
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <Box p={3} bg="bg.subtle" borderRadius="md" mt={2}>
                  <Controller
                    name="metadata"
                    control={control}
                    rules={{
                      validate: (value) => {
                        try {
                          JSON.parse(JSON.stringify(value))
                          return true
                        } catch {
                          return "Invalid JSON structure"
                        }
                      },
                    }}
                    render={({ field, fieldState }) => {
                      const [localJson, setLocalJson] = useState("")
                      const [parseError, setParseError] = useState<string | null>(null)

                      useEffect(() => {
                        setLocalJson(JSON.stringify(field.value ?? {}, null, 2))
                      }, [field.value])

                      return (
                        <Field.Root invalid={!!parseError || !!fieldState.error}>
                          <Textarea
                            value={localJson}
                            onChange={(e) => {
                              setLocalJson(e.target.value)
                              setParseError(null)
                              try {
                                const parsed = JSON.parse(e.target.value)
                                field.onChange(parsed)
                              } catch (error) {
                                if (error instanceof SyntaxError) {
                                  setParseError(error.message)
                                }
                              }
                            }}
                            onBlur={field.onBlur}
                            rows={10}
                            fontFamily="monospace"
                            fontSize="sm"
                            width="100%"
                          />
                          {parseError && (
                            <Field.ErrorText>{parseError}</Field.ErrorText>
                          )}
                          {fieldState.error && (
                            <Field.ErrorText>{fieldState.error.message}</Field.ErrorText>
                          )}
                        </Field.Root>
                      )
                    }}
                  />
                  <HStack mt={2} justify="flex-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setValue("metadata", alert.metadata ?? {})
                      }}
                    >
                      Reset
                    </Button>
                  </HStack>
                </Box>
              </CollapsibleContent>
            </CollapsibleRoot>
          )}

          <EditFields register={register} assignableUsers={assignableUsers} isNameDisabled={true} />

          <Separator />

          <NotesSection register={register} noteEvents={noteEvents} />
        </VStack>
      </Box>
    </Dialog>
  )
}
